/** Account area: data loading helpers and live updates from the user's live-account WS channel. */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComplianceState, UserEvent } from '@platform/shared';
import { api, ApiError } from '../lib/api';
import { alertFired } from './alerts';
import { createStore, useStore } from '../lib/store';
import { useChannel } from '../lib/ws';
import { actionLinkArrived, actionLinkClosed } from './actionLinks';
import { loadDocuments } from './documents';
import { addNotification, removeNotification } from './notifications';
import { loadMe, sessionStore, useMe } from './session';

type Topic = 'kyc' | 'deposit' | 'withdrawal' | 'walletVerification' | 'ticket' | 'balance' | 'depositRequest' | 'fundingTicket' | 'compliance';

/** Bumped when a WS event arrives so pages can refetch what changed. */
const versions = createStore<Record<Topic, number>>({ kyc: 0, deposit: 0, withdrawal: 0, walletVerification: 0, ticket: 0, balance: 0, depositRequest: 0, fundingTicket: 0, compliance: 0 });
const bump = (t: Topic) => versions.set((v) => ({ [t]: v[t] + 1 }));
export const useVersion = (t: Topic) => useStore(versions, (v) => v[t]);

/**
 * Mount once at the app root: follows the user's live account channel (KYC status shows in the top bar
 * everywhere; notifications feed the bell; trading restrictions update the trade-screen banner).
 */
export function useAccountEvents() {
  const me = useMe();
  const live = me?.accounts.find((a) => a.kind === 'live')?.id;
  useChannel(live ? `user:${live}` : null, (ev: UserEvent) => {
    if (ev.type === 'kyc') {
      sessionStore.set((s) => (s.me ? { me: { ...s.me, kycStatus: ev.status } } : {}));
      bump('kyc');
      bump('compliance');
    } else if (ev.type === 'compliance') {
      bump('compliance');
      bump('kyc');
    } else if (ev.type === 'deposit' || ev.type === 'crypto_payment') bump('deposit');
    else if (ev.type === 'withdrawal') bump('withdrawal');
    else if (ev.type === 'wallet_verification') bump('walletVerification');
    else if (ev.type === 'ticket') bump('ticket');
    else if (ev.type === 'balance') bump('balance');
    else if (ev.type === 'deposit_request') bump('depositRequest');
    else if (ev.type === 'funding_ticket') bump('fundingTicket');
    else if (ev.type === 'notification') addNotification(ev.notification);
    else if (ev.type === 'notification_removed') removeNotification(ev.id);
    else if (ev.type === 'action_link') actionLinkArrived(ev.link);
    else if (ev.type === 'action_link_closed') actionLinkClosed(ev.id, ev.status);
    else if (ev.type === 'documents_changed') void loadDocuments();
    else if (ev.type === 'alert') {
      alertFired(ev.event);
    } else if (ev.type === 'restrictions') {
      sessionStore.set((s) =>
        s.me ? { me: { ...s.me, tradingStatus: ev.tradingStatus, withdrawalStatus: ev.withdrawalStatus, tradingStatusReason: ev.reason } } : {},
      );
    } else if (ev.type === 'demo') {
      void loadMe().catch(() => {});
    } else if (ev.type === 'leverage') {
      sessionStore.set((s) => (s.me ? { me: { ...s.me, maxLeverage: ev.maxLeverage } } : {}));
    }
  });
}

/** GET a path, reload on demand and whenever any of `deps` change. */
export function useLoad<T>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const reload = useCallback(async () => {
    if (!path) return;
    const my = ++seq.current;
    setLoading(true);
    try {
      const d = await api.get<T>(path);
      if (my === seq.current) {
        setData(d);
        setError(null);
      }
    } catch (e) {
      if (my === seq.current) setError(e instanceof ApiError ? e.message : 'Could not load data');
    } finally {
      if (my === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, error, loading, reload, setData };
}

/**
 * Deposit threshold, source of funds and proof-of-address state. Refetched on compliance, KYC and
 * deposit events, since a credited deposit can change it.
 */
export function useCompliance() {
  const c = useVersion('compliance');
  const d = useVersion('deposit');
  const r = useVersion('depositRequest');
  return useLoad<ComplianceState>('/api/compliance', [c, d, r]).data;
}
